import { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, Pencil, Trash2, Search } from "lucide-react";
import { toast } from "sonner";
import StickyHeader from "@/components/StickyHeader";

export default function Prodi() {
    const [items, setItems] = useState([]);
    const [dialog, setDialog] = useState(false);
    const [form, setForm] = useState({ nama_prodi: "", jenjang: "Sp1" });
    const [editing, setEditing] = useState(null);
    const [confirmDel, setConfirmDel] = useState(null);
    const [jenjangFilter, setJenjangFilter] = useState("all");
    const [q, setQ] = useState("");

    const load = async () => {
        try { const { data } = await api.get("/prodi"); setItems(data); } catch {}
    };
    useEffect(() => { load(); }, []);

    const submit = async () => {
        try {
            if (editing) await api.put(`/prodi/${editing.id}`, form);
            else await api.post("/prodi", form);
            toast.success(editing ? "Prodi diperbarui" : "Prodi ditambahkan");
            setDialog(false); setEditing(null); setForm({ nama_prodi: "", jenjang: "Sp1" });
            load();
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    };

    const del = async () => {
        try {
            await api.delete(`/prodi/${confirmDel.id}`);
            toast.success("Prodi dihapus");
            setConfirmDel(null); load();
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    };

    const filtered = items.filter((p) =>
        (jenjangFilter === "all" || p.jenjang === jenjangFilter) &&
        p.nama_prodi.toLowerCase().includes(q.trim().toLowerCase())
    );

    // Subjudul dihitung otomatis dari data (bukan angka statis) -- supaya tetap akurat
    // berapa pun jumlah prodi per jenjang, termasuk saat jenjang baru (Koas) ditambah.
    const jenjangSubtitle = useMemo(() => {
        const counts = { Sp1: 0, Sp2: 0, Koas: 0 };
        items.forEach((p) => { if (p.jenjang in counts) counts[p.jenjang]++; });
        const parts = ["Sp1", "Sp2", "Koas"]
            .filter((j) => counts[j] > 0)
            .map((j) => `${counts[j]} prodi ${j}`);
        if (parts.length === 0) return "Belum ada prodi";
        if (parts.length === 1) return parts[0];
        return `${parts.slice(0, -1).join(", ")}, dan ${parts[parts.length - 1]}`;
    }, [items]);

    return (
        <div className="max-w-5xl mx-auto px-6 lg:px-8 py-8 space-y-4">
            <StickyHeader>
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
                <div>
                    <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">Master Data</div>
                    <h1 className="text-3xl font-extrabold tracking-tight text-slate-900" style={{ fontFamily: "Plus Jakarta Sans" }}>
                        Program Studi
                    </h1>
                    <p className="text-sm text-slate-500 mt-1">{jenjangSubtitle} di RSCM.</p>
                </div>
                <div className="flex flex-wrap gap-2">
                    <div className="relative w-full sm:w-56">
                        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                        <Input
                            data-testid="prodi-search-input"
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            placeholder="Cari nama prodi..."
                            className="pl-9 h-10"
                        />
                    </div>
                    <Select value={jenjangFilter} onValueChange={setJenjangFilter}>
                        <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">Semua Jenjang</SelectItem>
                            <SelectItem value="Sp1">Sp1 saja</SelectItem>
                            <SelectItem value="Sp2">Sp2 saja</SelectItem>
                            <SelectItem value="Koas">Koas saja</SelectItem>
                        </SelectContent>
                    </Select>
                    <Button data-testid="prodi-add-button" onClick={() => { setEditing(null); setForm({ nama_prodi: "", jenjang: "Sp1" }); setDialog(true); }} className="bg-[#00B9AD] hover:bg-[#60C0D0]">
                        <Plus className="w-4 h-4 mr-2" /> Tambah Prodi
                    </Button>
                </div>
            </div>
            </StickyHeader>

            <Card className="border-slate-200 shadow-sm overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full compact">
                        <thead className="bg-slate-100">
                            <tr className="text-left text-xs uppercase tracking-wider text-slate-600 font-semibold">
                                <th>Nama Program Studi</th>
                                <th>Jenjang</th>
                                <th className="text-right pr-4">Aksi</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map((p) => (
                                <tr key={p.id} className="border-t border-slate-200 hover:bg-slate-50">
                                    <td className="font-semibold text-slate-800">{p.nama_prodi}</td>
                                    <td><Badge variant="outline">{p.jenjang}</Badge></td>
                                    <td className="text-right pr-2">
                                        <Button size="sm" variant="ghost" onClick={() => { setEditing(p); setForm({ nama_prodi: p.nama_prodi, jenjang: p.jenjang }); setDialog(true); }}>
                                            <Pencil className="w-4 h-4" />
                                        </Button>
                                        <Button size="sm" variant="ghost" className="text-rose-600" onClick={() => setConfirmDel(p)}>
                                            <Trash2 className="w-4 h-4" />
                                        </Button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                <div className="px-4 py-2 border-t bg-slate-50 text-xs text-slate-500">
                    Total: <span className="font-bold text-slate-800">{filtered.length}</span> prodi
                </div>
            </Card>

            <Dialog open={dialog} onOpenChange={setDialog}>
                <DialogContent>
                    <DialogHeader><DialogTitle>{editing ? "Edit Prodi" : "Tambah Prodi"}</DialogTitle></DialogHeader>
                    <div className="space-y-3">
                        <div>
                            <Label className="text-xs font-semibold uppercase text-slate-600">Nama Prodi</Label>
                            <Input data-testid="prodi-form-nama" value={form.nama_prodi} onChange={(e) => setForm({ ...form, nama_prodi: e.target.value })} />
                        </div>
                        <div>
                            <Label className="text-xs font-semibold uppercase text-slate-600">Jenjang</Label>
                            <Select value={form.jenjang} onValueChange={(v) => setForm({ ...form, jenjang: v })}>
                                <SelectTrigger><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="Sp1">Sp1</SelectItem>
                                    <SelectItem value="Sp2">Sp2</SelectItem>
                                    <SelectItem value="Koas">Koas</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDialog(false)}>Batal</Button>
                        <Button onClick={submit} className="bg-[#00B9AD] hover:bg-[#60C0D0]" data-testid="prodi-form-submit">Simpan</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <AlertDialog open={!!confirmDel} onOpenChange={(o) => !o && setConfirmDel(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Hapus prodi?</AlertDialogTitle>
                        <AlertDialogDescription>
                            {confirmDel?.nama_prodi} ({confirmDel?.jenjang}) akan dihapus.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Batal</AlertDialogCancel>
                        <AlertDialogAction className="bg-rose-600 hover:bg-rose-700" onClick={del}>Hapus</AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
